import { getFileNameFromPath, ImageThumbnail, MaterialIcon } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { LabelSmall } from 'baseui/typography';
import React from 'react';
import { StyleObject } from 'styletron-react';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { getS3DownloadUrlByKey } from '../../services/appService';
import { FileDisplay } from '../../types/app';
import { downloadBlob, isValidImage } from '../../utils/files';

type Props = Readonly<{
  display: FileDisplay;
  disableDownload?: boolean;
  fileStyle?: StyleObject;
  imageStyle?: React.CSSProperties;
  onImageClick?: (display: FileDisplay) => void;
}>;

function FileThumbnailPreview({ display, imageStyle, onImageClick, fileStyle, disableDownload = false }: Props) {
  const { theme, css } = useStyles();
  const { showFailFeedback } = useFeedback();

  if (!display.url) return null;

  const isImage = isValidImage(display.key);
  const filename = getFileNameFromPath(display.key);
  const coreName = filename.substring(filename.indexOf('.') + 1);

  const download = async (key: string) => {
    const { file, error } = await getS3DownloadUrlByKey(key);
    if (error) {
      showFailFeedback(error);
    } else if (file) {
      downloadBlob(file, getFileNameFromPath(key));
    }
  };

  if (isImage) {
    return (
      <ImageThumbnail
        url={display.url}
        onClick={() => onImageClick?.(display)}
        imageStyle={{
          height: '150px',
          width: '100%',
          objectFit: 'cover',
          cursor: onImageClick ? 'pointer' : 'auto',
          background: theme.colors.buttonTertiaryHover,
          ...imageStyle,
        }}
      />
    );
  }

  return (
    <Block
      className={css({
        height: '150px',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.buttonTertiaryHover,
        cursor: disableDownload ? 'auto' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.sizing.scale100,
        ...fileStyle,
      })}
      onClick={disableDownload ? undefined : () => download(display.key)}
    >
      <MaterialIcon
        name="attachment"
        size="compact"
        color={theme.colors.contentSecondary}
      />
      <LabelSmall color={theme.colors.contentSecondary}>{coreName}</LabelSmall>
    </Block>
  );
}

export default FileThumbnailPreview;
